import { useMemo } from 'react';
import isEqual from 'lodash.isequal';
import { toast } from 'react-toastify';
import FormSmallModal, { InputRow } from '../global/FormSmallModal';
import useBusiness from '../../hooks/businesses/useBusiness';
import { QueueSettingsPayload, queueSettingsSchema } from '../../types/queue';
import useUpdateQueueSettings from '../../hooks/queue/useUpdateQueueSettings';
import { getCheckInURL, onDownloadQRCode } from '../../helpers/queue';
import useUser from '../../hooks/global/useUser';
import { isUserAdminOrManager } from '../../utils/global/helper';
import useQueueSettings from '../../hooks/queue/useQueueSettings';

interface Props {
  open: boolean;
  onClose: () => void;
}

function QueueSettingsFormModal({ open, onClose }: Props) {
  const { user } = useUser();
  const { business } = useBusiness();
  const { mutateAsync: updateQueueSettings } = useUpdateQueueSettings({
    onSuccess: () => {
      toast.success('Queue settings updated!');
    },
    onError: error => {
      toast.error(error);
    },
  });
  const initialValues = useQueueSettings();

  const isEditingAllowed = !!user && isUserAdminOrManager(user);

  const inputRows = useMemo(() => {
    const initialInputRows: InputRow<QueueSettingsPayload>[] = [
      [
        {
          id: 'shopCheckInLink',
          inputType: 'copy',
          label: 'Shop check-in link:',
          value: getCheckInURL(business),
          name: 'shopCheckInLink' as any,
          footerAction: {
            label: 'Download QR Code',
            onClick: () => {
              onDownloadQRCode(business);
            },
          },
        },
      ],
    ];
    if (!isEditingAllowed) return initialInputRows;

    initialInputRows.push(
      [
        {
          inputType: 'select',
          label: 'Collect tattoo details from the customer?',
          isLabelMultiline: true,
          name: 'collectServiceDetails',
          options: [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Collect preferred artist from the customer?',
          isLabelMultiline: true,
          name: 'collectArtistPreference',
          options: [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'When should the customer complete the waiver?',
          name: 'waiverBehavior',
          options: [
            {
              label: 'While checking-in',
              value: 'IMMEDIATE',
              badge: {
                text: 'Recommended',
                colorScheme: 'secondary',
              },
            },
            { label: 'When artist is assigned', value: 'ASSIGNED' },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Allow artists to pull from the walk-in queue?',
          name: 'allowArtistsToPull',
          options: [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ],
          autoWidth: false,
        },
      ],
    );
    return initialInputRows;
  }, [business, isEditingAllowed]);

  return (
    <FormSmallModal
      open={open}
      onClose={onClose}
      allowUntouched={false}
      form={{
        title: isEditingAllowed ? 'Walk-in Queue Settings' : 'Check-in Link',
        initialValues,
        inputRows,
        onSubmit: async values => {
          if (!isEditingAllowed) return;
          const isChanged = !isEqual(values, initialValues);
          if (!isChanged) return;

          await updateQueueSettings(values);

          onClose();
        },
        validationSchema: queueSettingsSchema,
        submitButtonText: isEditingAllowed ? 'Save' : undefined,
      }}
      maxWidth={600}
    />
  );
}

export default QueueSettingsFormModal;
